import { Link, useLocation, useNavigate } from "react-router-dom";

/**
 * "Back" in a public page's top bar.
 *
 * <p><b>It is only a history control when there IS history.</b> A profile link is the thing people
 * share, so a large share of arrivals on this page are the first entry in the tab — and
 * `navigate(-1)` there walks the visitor out of the site, back to whatever they came from or to a
 * blank tab. React Router marks that first entry with `key === "default"`, so when it is the one we
 * are on, this renders a plain link to the marketplace instead.</p>
 *
 * <p>The label changes with it. "Back" is a promise about where you will land, and it should not be
 * made when the answer is "somewhere else entirely".</p>
 */
export default function BackButton({
  fallbackTo = "/",
  fallbackLabel = "Back to the marketplace",
  color = "#243139",
}: {
  /** Where to go when there is nothing to go back to. */
  fallbackTo?: string;
  fallbackLabel?: string;
  /** Ink colour, so this can sit on either of the two public palettes. */
  color?: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const canGoBack = location.key !== "default";

  const style: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "none",
    border: "none",
    padding: "8px 10px 8px 0",
    margin: 0,
    font: "inherit",
    fontSize: 14,
    fontWeight: 600,
    color,
    textDecoration: "none",
    cursor: "pointer",
  };

  const arrow = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  );

  if (!canGoBack) {
    return (
      <Link to={fallbackTo} data-testid="back-button" style={style}>
        {arrow}
        {fallbackLabel}
      </Link>
    );
  }

  return (
    <button type="button" data-testid="back-button" onClick={() => navigate(-1)} style={style}>
      {arrow}
      Back
    </button>
  );
}
